// 月の資産額
export type MonthlyAssetAmount = {
  // YYYYMM 形式
  yearMonth: string;
  asset: number;
};

// 利率を利用するか判定
// initAssetAmount 当初資産額 [円]
// targetAssetAmount 目標金額 [円]
// months 月数（現在月～目標時間年前までの月数）
// reserveAmount 毎月の積立金額 [円]
export const needsInterestRate = (
  initAssetAmount: number,
  targetAssetAmount: number,
  months: number,
  reserveAmount: number
): boolean => {
  return initAssetAmount + reserveAmount * months < targetAssetAmount;
};

// みんかぶ必要年利算出
// months 運用期間月数（現在月～目標時間年前までの月数）
// reserveAmount 毎月の積立金額 [円]  マイナス指定
// initAssetAmount 当初資産額 [円]   マイナス指定
// [targetAssetAmount 目標金額 [円]]
// [type 支払期日]  値不明
// [guess 推定値]
export const calculateRequiredAnnualInterestRate = (
  months: number,
  reserveAmount: number,
  initAssetAmount: number,
  targetAssetAmount = 0,
  type = 0,
  guess = 0.01
): number => {
  const FINANCIAL_MAX_ITERATIONS = 128; // Bet accuracy with 128
  const FINANCIAL_PRECISION = 0.0000001; // 1.0e-8

  let y;
  let y0;
  let y1;
  let x0;
  let x1 = 0;
  let f = 0;
  let i = 0;
  let rate = guess;
  if (Math.abs(rate) < FINANCIAL_PRECISION) {
    y =
      initAssetAmount * (1 + months * rate) +
      reserveAmount * (1 + rate * type) * months +
      targetAssetAmount;
  } else {
    f = Math.exp(months * Math.log(1 + rate));
    y =
      initAssetAmount * f +
      reserveAmount * (1 / rate + type) * (f - 1) +
      targetAssetAmount;
  }
  y0 = initAssetAmount + reserveAmount * months + targetAssetAmount;
  y1 =
    initAssetAmount * f +
    reserveAmount * (1 / rate + type) * (f - 1) +
    targetAssetAmount;

  // find root by Newton secant method
  i = x0 = 0.0;
  x1 = rate;
  while (
    Math.abs(y0 - y1) > FINANCIAL_PRECISION &&
    i < FINANCIAL_MAX_ITERATIONS
  ) {
    rate = (y1 * x0 - y0 * x1) / (y1 - y0);
    x0 = x1;
    x1 = rate;

    if (Math.abs(rate) < FINANCIAL_PRECISION) {
      y =
        initAssetAmount * (1 + months * rate) +
        reserveAmount * (1 + rate * type) * months +
        targetAssetAmount;
    } else {
      f = Math.exp(months * Math.log(1 + rate));
      y =
        initAssetAmount * f +
        reserveAmount * (1 / rate + type) * (f - 1) +
        targetAssetAmount;
    }

    y0 = y1;
    y1 = y;
    ++i;
  }
  return rate;
};

// 年単位の計画資産額の算出
// currentAssetAmount 資産額 [円]
// months 運用期間月数
// reserveAmount 毎月の積立金額 [円]
// annualInterestRate 利率 [%でなく単位なしの割合] なしの場合 0 を指定
export const calculateAnnualPlannedAssetAmount = (
  currentAssetAmount: number,
  months: number,
  reserveAmount: number,
  annualInterestRate: number
): number => {
  // (資産 ＋ (積立額 × 月数)) × (1 ＋ (年利 / 12 * 月数)), 小数点以下切り上げ
  return Math.ceil(
    (currentAssetAmount + reserveAmount * months) *
      (1 + (annualInterestRate / 12) * months)
  );
};

// 月単位の計画資産額の算出
// currentAssetAmount 資産額 [円]
// annualPlannedAssetAmount 年間計画資産額 [円]
// annualMonths 年間月数
// targetMonthsOfYear 当年の何ヶ月目か
export const calculateMonthlyPlannedAssetAmount = (
  currentAssetAmount: number,
  annualPlannedAssetAmount: number,
  annualMonths: number,
  targetMonthsOfYear: number
): number => {
  // (計画資産額 - 開始資産額) / 当年合計月数 × 当年の何ヶ月目か 小数点以下切り上げ + 開始資産額
  return (
    Math.ceil(
      ((annualPlannedAssetAmount - currentAssetAmount) / annualMonths) *
        targetMonthsOfYear
    ) + currentAssetAmount
  );
};

// 計画資産額を達成するための毎月の資産額の算出
// initAssetAmount 当初資産額 [円]
// months 月数（現在月～目標時間年前までの月数
// reserveAmount 毎月の積立額 [円]
// annualInterestRate 利率 [%でなく単位なしの割合] なしの場合 0 を指定
// [baseDate 基準日（開始日） 指定のない場合当日]
export const calculateMonthlyAssetAmounts = (
  initAssetAmount: number,
  months: number,
  reserveAmount: number,
  annualInterestRate: number,
  baseDate: Date | null = null
): MonthlyAssetAmount[] => {
  const assets: MonthlyAssetAmount[] = [];
  // 現在日から初年の年末までの月数を算出 年間月数＜月数 の場合は 月数まで
  const startDate = baseDate ? baseDate : new Date();
  // 当月を含む、`12 - (startDate.getMonth() + 1) - 1`を最適化した式を記述
  const firstAnnualMonths = 12 - startDate.getMonth();
  let annualMonths = Math.min(firstAnnualMonths, months);
  // 初年の月数で年単位の計画資産額の算出
  let annualPlannedAssetAmount = calculateAnnualPlannedAssetAmount(
    initAssetAmount,
    annualMonths,
    reserveAmount,
    annualInterestRate
  );

  // 初年の月ごとに 月単位の計画資産額の算出 年間月数＜月数 の場合は 月数まで
  let year = startDate.getFullYear();
  let startMonth = startDate.getMonth() + 1;
  for (let n = 0; n < annualMonths; n++) {
    assets.push({
      yearMonth: `${year.toString()}${(startMonth + n)
        .toString()
        .padStart(2, '0')}`,
      asset: calculateMonthlyPlannedAssetAmount(
        initAssetAmount,
        annualPlannedAssetAmount,
        annualMonths,
        n + 1
      ),
    });
  }

  // 残りの月数があるまで年ごとに
  let leftMonths = months - annualMonths;
  while (leftMonths > 0) {
    // 年間の月数で年単位の計画資産額の算出 年間月数＜月数 の場合は 月数まで
    annualMonths = Math.min(leftMonths, 12);
    leftMonths = leftMonths - annualMonths;
    const prevAnnualPlannedAssetAmount = annualPlannedAssetAmount;

    annualPlannedAssetAmount = calculateAnnualPlannedAssetAmount(
      prevAnnualPlannedAssetAmount,
      annualMonths,
      reserveAmount,
      annualInterestRate
    );

    // 年間の月ごとに 月単位の計画資産額の算出 年間月数＜月数 の場合は 月数まで
    year++;
    startMonth = 1;
    for (let n = 0; n < annualMonths; n++) {
      assets.push({
        yearMonth: `${year.toString()}${(startMonth + n)
          .toString()
          .padStart(2, '0')}`,
        asset: calculateMonthlyPlannedAssetAmount(
          prevAnnualPlannedAssetAmount,
          annualPlannedAssetAmount,
          annualMonths,
          n + 1
        ),
      });
    }
  }

  return assets;
};

/**
 * 値と基となる値の比率を返す
 * @param base 基となる値
 * @param value 比率を取る値
 * @returns 比率（1 = 1%）
 */
export function calculateRate(base: number, value: number): number {
  return (value / base) * 100;
}

/**
 * 値と元となる値の乖離率を返す
 * @param base 元なる値
 * @param value 乖離率をとる値
 * @returns 乖離率（1 = 1%）
 */
export function calculateDeviationRate(base: number, value: number): number {
  return ((value - base) / base) * 100;
}

/**
 * 値を百分率にする
 * @param value %に変換する値（1 = 100%）
 * @param decimalOfNumber 小数点桁数
 * @returns %に変換した値（1 = 1%）
 */
export function convertPercentage(
  value: number,
  decimalOfNumber: number
): number {
  return Number(
    (
      Math.ceil(
        Number((value * 100 * 10 ** decimalOfNumber).toFixed(decimalOfNumber))
      ) /
      10 ** decimalOfNumber
    ).toFixed(decimalOfNumber)
  );
}
